/**
 * Client-side ordering for list screens. A list declares its options (label + comparator) and
 * `sortItems` applies the selected one to the rows it already loaded. The comparators are built
 * from small helpers so every list orders text, numbers and dates the same way:
 * Spanish collation (accents and case ignored), and rows without a value always last.
 */

export interface SortOption<T> {
  value: string;
  label: string;
  compare: (a: T, b: T) => number;
}

export type Direction = 'asc' | 'desc';

type Getter<T, V> = (item: T) => V | undefined | null;

const isMissing = (v: unknown): boolean =>
  v === undefined || v === null || v === '' || (typeof v === 'number' && Number.isNaN(v));

function by<T, V>(get: Getter<T, V>, cmp: (a: V, b: V) => number, dir: Direction): (a: T, b: T) => number {
  return (a, b) => {
    const x = get(a);
    const y = get(b);
    const missingX = isMissing(x);
    const missingY = isMissing(y);
    if (missingX || missingY) return missingX === missingY ? 0 : missingX ? 1 : -1;
    const result = cmp(x as V, y as V);
    return dir === 'asc' ? result : -result;
  };
}

export const byText = <T>(get: Getter<T, string>, dir: Direction = 'asc') =>
  by(get, (x, y) => x.localeCompare(y, 'es', { sensitivity: 'base' }), dir);

export const byNumber = <T>(get: Getter<T, number>, dir: Direction = 'asc') =>
  by(get, (x, y) => x - y, dir);

/** ISO-8601 strings (dates or timestamps) order correctly as plain text. */
export const byDate = <T>(get: Getter<T, string>, dir: Direction = 'asc') =>
  by(get, (x, y) => (x < y ? -1 : x > y ? 1 : 0), dir);

/** Run comparators in order; the next one only breaks ties of the previous. */
export const thenBy =
  <T>(...comparators: ((a: T, b: T) => number)[]) =>
  (a: T, b: T): number => {
    for (const compare of comparators) {
      const result = compare(a, b);
      if (result !== 0) return result;
    }
    return 0;
  };

/** A new array ordered by the option with this value (the first option when it is unknown). */
export function sortItems<T>(items: readonly T[], options: readonly SortOption<T>[], value: string): T[] {
  const option = options.find((o) => o.value === value) ?? options[0];
  return option ? [...items].sort(option.compare) : [...items];
}
